/* eslint-disable no-console */
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import Spinner from '../Spinner';

export default class Button extends React.PureComponent {
    static propTypes = {
        /** Contents of the button, usually text */
        children: PropTypes.node,

        /** Sets the html button type */
        type: PropTypes.oneOf(["button", "submit", "reset"]),

        /** Sets the visual appearance */
        appearance: PropTypes.oneOf([
            'primary',
            'secondary',
            'tertiary',
            'tertiary-alt',
            'tertiary-white',
            'solid-white',
            'subtle',
            'link',
            'danger',
            'subtle-danger',
            'pill',
            'icon-white',
            'icon-circular'
        ]),

        /** Sets the size including font-size, padding, and height */
        size: PropTypes.oneOf(['xxs', 'xs', 'sm', 'md', 'lg']),

        /** If true, the width is 100% */
        shouldFitContainer: PropTypes.bool,

        /** Converts the button to a link with the given href */
        href: PropTypes.string,

        /** Converts the button to a React Router Link with the given to */
        to: PropTypes.string,

        /** Icon element to render before the children */
        iconBeforeElement: PropTypes.element,

        /** Icon element to render after the children */
        iconAfterElement: PropTypes.element,

        /** If true, the contents are replaced with a loading spinner */
        isLoading: PropTypes.bool,

        /** If true, the button is disabled */
        isDisabled: PropTypes.bool,

        /** If true, the button will gain focus when it mounts */
        shouldFocusOnMount: PropTypes.bool,

        /** Text for a screen reader to announce before any children */
        screenReaderTextBefore: PropTypes.string,

        /** Text for a screen reader to announce after any children */
        screenReaderTextAfter: PropTypes.string,

        /** Normal click handler, but disabled if the isLoading or isDisabled is true. */
        onClick: PropTypes.func,

        /** Added to existing classes */
        className: PropTypes.string,

        /** Ref passed to the button html element  */
        innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
    };

    static defaultProps = {
        children: undefined,
        type: 'button',
        appearance: 'primary',
        size: 'md',
        shouldFitContainer: false,
        href: undefined,
        to: undefined,
        iconBeforeElement: undefined,
        iconAfterElement: undefined,
        isLoading: false,
        isDisabled: false,
        shouldFocusOnMount: false,
        screenReaderTextBefore: undefined,
        screenReaderTextAfter: undefined,
        onClick: undefined,
        className: undefined
    };

    constructor(props) {
        super(props);

        this.buttonRef = props.innerRef ? props.innerRef : React.createRef();
    }

    componentDidMount() {
        const { shouldFocusOnMount } = this.props;
        this.timeout = setTimeout(() => {
            if (shouldFocusOnMount && this.buttonRef) {
                if (this.buttonRef.current) {
                    this.buttonRef.current.focus();
                }
                else {
                    this.buttonRef.focus();
                }
            }
        }, 0);
    }

    componentWillUnmount() {
        // Safety in case of render thrashing
        clearTimeout(this.timeout);
    }

    getBaseElement = () => {
        const { to, href } = this.props;

        if (typeof to === 'string' && to.length) {
            return Link;
        }

        if (typeof href === 'string' && href.length) {
            return 'a';
        }

        return 'button';
    };

    getType = () => {
        const { to, href, type } = this.props;

        if (
            (typeof to === 'string' && to.length) ||
            (typeof href === 'string' && href.length)
        ) {
            return null;
        }

        return type;
    };

    handleKeyDown = (event) => {
        const key = event.key || event.code || event.keyCode;
        switch (key) {
            case ' ' || 'Space' || 32:
                event.preventDefault();
                window.location = event.target.href;
                break;
            default:
                break;
        }
    };

    handleClick = (e) => {
        const { isLoading, isDisabled, onClick } = this.props;

        if (!(isLoading || isDisabled) && onClick) {
            onClick(e);
        }
    }

    render() {
        const {
            children,
            type,
            appearance,
            size,
            isLoading,
            isDisabled,
            iconBeforeElement,
            iconAfterElement,
            screenReaderTextBefore,
            screenReaderTextAfter,
            shouldFitContainer,
            shouldFocusOnMount,
            onClick,
            className,
            innerRef,
            ...rest
        } = this.props;

        if (process.env.NODE_ENV === 'development' && isDisabled && (!!this.props.to || !!this.props.href)) {
            console.error('** Sarsa Link Buttons can not be disabled. ** \n  If you need to perform an action and then navigate, remove the "to" or "href" prop and use React Router’s history prop to navigate after the action is complete.');
        }

        const ButtonElement = this.getBaseElement();
        const iconIsOnlyChild = !!(
            (iconBeforeElement && !iconAfterElement && !children) ||
            (!iconBeforeElement && iconAfterElement && !children)
        );
        const srSeparator = iconIsOnlyChild ? null : ' ';
        const classes = cx('sarsa-button', className, {
            'sarsa-button-primary': appearance === 'primary',
            'sarsa-button-secondary': appearance === 'secondary',
            'sarsa-button-tertiary': appearance === 'tertiary',
            'sarsa-button-tertiary-alt': appearance === 'tertiary-alt',
            'sarsa-button-tertiary-white': appearance === 'tertiary-white',
            'sarsa-button-solid-white': appearance === 'solid-white',
            'sarsa-button-icon-circular': appearance === 'icon-circular',
            'sarsa-button-link': appearance === 'link',
            'sarsa-button-subtle': appearance === 'subtle',
            'sarsa-button-danger': appearance === 'danger',
            'sarsa-button-pill': appearance === 'pill',
            'sarsa-button-subtle-danger': appearance === 'subtle-danger',
            'sarsa-button-icon-white': appearance === 'icon-white',
            'sarsa-button-xxs': size === 'xxs',
            'sarsa-button-xs': size === 'xs',
            'sarsa-button-sm': size === 'sm',
            'sarsa-button-md': size === 'md',
            'sarsa-button-lg': size === 'lg',
            'sarsa-button-fit-container': shouldFitContainer,
            'sarsa-button-is-loading': isLoading,
            'sarsa-button-disabled': isDisabled
        });

        let spinnerColor;

        switch (appearance) {
            case 'tertiary':
            case 'link':
                spinnerColor = 'brand';
                break;
            case 'tertiary-alt':
            case 'subtle':
            case 'pill':
                spinnerColor = 'subtle';
                break;
            case 'subtle-danger':
                spinnerColor = 'danger';
                break;
            default:
                spinnerColor = 'inverted';
        }

        let spinnerSize;

        switch (size) {
            case 'lg':
                spinnerSize = 24;
                break;
            case 'md':
                spinnerSize = 20;
                break;
            case 'sm':
            case 'xs':
                spinnerSize = 16;
                break;
            case 'xxs':
                spinnerSize = 12;
                break;
            default:
                spinnerSize = 20;
        }

        const handleKeyDown = (typeof this.props.to === 'string' && this.props.to.length) || (typeof this.props.href === 'string' && this.props.href.length) ? this.handleKeyDown : null;

        return (
            <ButtonElement
                data-component="Button"
                ref={this.buttonRef}
                type={this.getType()}
                disabled={isDisabled}
                className={classes}
                onClick={this.handleClick}
                onKeyDown={handleKeyDown}
                {...rest}>
                {isLoading && (
                    <span className="sarsa-button-spinner-wrapper">
                        <Spinner
                            isSpinning={isLoading}
                            size={spinnerSize}
                            color={spinnerColor} />
                    </span>
                )}

                <span className={cx('sarsa-button-inner-wrapper', { 'is-loading': isLoading })}>
                    {iconBeforeElement && (
                        <span
                            aria-hidden="true"
                            className={cx('sarsa-button-icon-content', 'left-icon', {
                                'is-only-child': iconIsOnlyChild
                            })}>
                            {iconBeforeElement}
                        </span>
                    )}

                    <span className="sarsa-button-content">
                        {screenReaderTextBefore && (
                            <span className="rec-sr-only">
                                {screenReaderTextBefore}
                                {srSeparator}
                            </span>
                        )}

                        {children}

                        {screenReaderTextAfter && (
                            <span className="rec-sr-only">
                                {srSeparator}
                                {screenReaderTextAfter}
                            </span>
                        )}
                    </span>

                    {iconAfterElement && (
                        <span
                            aria-hidden="true"
                            className={cx('sarsa-button-icon-content', 'right-icon', {
                                'is-only-child': iconIsOnlyChild
                            })}>
                            {iconAfterElement}
                        </span>
                    )}
                </span>
            </ButtonElement>
        );
    }
}
